import dayjs from 'dayjs';
import { useState } from 'react';
import DatePicker from 'react-datepicker';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CalendarIcon } from '../../assets';
import { MONTH_RANGE_DROPDOWN_OPTIONS } from '../../config/constants';
import { GenericDropdown } from './GenericDropdown';

type NumberFormatFromToProps = {
  valueFrom?: string;
  valueTo?: string;
  onChangeFunction: (date_from: string, date_to: string) => any;
  className?: string;
  hideDropdown?: boolean;
  style?: 'blue' | 'white';
};

type RangeMonthForm = {
  from: string;
  to: string;
  dropdown: string;
};

const MonthDateFromTo = ({
  valueFrom,
  valueTo,
  onChangeFunction,
  className,
  hideDropdown,
  style = 'blue',
}: NumberFormatFromToProps) => {
  const { i18n, t } = useTranslation('common');

  const [open, setOpen] = useState<boolean>(false);

  const { setValue, watch, control } = useForm<RangeMonthForm>({
    defaultValues: {
      dropdown:
        MONTH_RANGE_DROPDOWN_OPTIONS.find(
          (o) =>
            o.values && o.values[0] === valueFrom && o.values[1] === valueTo,
        )?.value || 'personalized',
      from: valueFrom,
      to: valueTo,
    },
  });

  const { from, to } = watch();

  const handleChange = ([newStartDate, newEndDate]: [
    Date | null,
    Date | null,
  ]) => {
    setValue('from', newStartDate ? dayjs(newStartDate).format('MM/YYYY') : '');
    setValue('to', newEndDate ? dayjs(newEndDate).format('MM/YYYY') : '');
    if (newStartDate && newEndDate) setOpen(false);
  };

  const onClose = () => {
    if (valueFrom !== from || valueTo !== to) {
      onChangeFunction(from || '', to || '');
    }
    setValue(
      'dropdown',
      MONTH_RANGE_DROPDOWN_OPTIONS.find(
        (o) => o.values && o.values[0] === from && o.values[1] === to,
      )?.value || 'personalized',
    );
    setOpen(false);
  };

  const styleClassnames =
    style === 'blue'
      ? '!bg-influentia-light-blue text-dark-blue'
      : '!bg-white border border-influentia-light-gray text-influentia-black';

  return (
    <div
      className={`flex flex-row gap-x-2 items-center justify-center  ${className}`}
    >
      {!hideDropdown && (
        <GenericDropdown
          options={MONTH_RANGE_DROPDOWN_OPTIONS.map((o) => ({
            ...o,
            label: t(o.label),
          }))}
          height="h-[41px]"
          isClearable={false}
          control={control}
          className={styleClassnames}
          name={`dropdown`}
          onChangeManual={(value: any) => {
            if (!value.values) return;
            const fromDropdown = value.values[0];
            const toDropdown = value.values[1];
            setValue('from', fromDropdown);
            setValue('to', toDropdown);
            if (valueFrom !== fromDropdown || valueTo !== toDropdown) {
              onChangeFunction(fromDropdown || '', toDropdown || '');
            }
          }}
        />
      )}
      <div className="relative w-full">
        <DatePicker
          className={`pl-8 ${styleClassnames} px-3 min-h-[41px] min-w-[220px] w-full py-[8px] text-sm font-medium focus:outline-none focus:ring-0 rounded-[4px] focus-visible:outline-none`}
          selected={from ? dayjs(from, 'MM/YYYY').toDate() : undefined}
          onChange={handleChange}
          onCalendarClose={onClose}
          onInputClick={() => setOpen(true)}
          onClickOutside={() => setOpen(false)}
          open={open}
          selectsRange
          startDate={from ? dayjs(from, 'MM/YYYY').toDate() : undefined}
          endDate={to ? dayjs(to, 'MM/YYYY').toDate() : undefined}
          dateFormat="MM/yyyy"
          showMonthYearPicker
          fixedHeight
          locale={i18n.language}
          placeholderText="From - To"
          wrapperClassName="w-full"
        />
        <div
          className={`absolute left-2 top-0 h-[41px] flex items-center justify-center cursor-pointer`}
          onClick={() => setOpen(true)}
        >
          <CalendarIcon className="text-influentia-black size-[18px]" />
        </div>
      </div>
    </div>
  );
};

export default MonthDateFromTo;
